// src/pages/student/Profile.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card, Button, Badge, Spinner, Row, Col, Form, Modal, InputGroup, Alert, Dropdown
} from "react-bootstrap";
import {
  doc, getDoc, onSnapshot, updateDoc, serverTimestamp
} from "firebase/firestore";
import { db, storage } from "../../services/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  getAuth,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  updatePassword,
  EmailAuthProvider,
  updateEmail,
} from "firebase/auth";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { useBrand, hexToRgba } from "./_brand";

/* ---------------- helpers ---------------- */
const safe = (v, fb = "—") => (v ? v : fb);
const toJSDate = (d) => (d?.toDate ? d.toDate() : d ? new Date(d) : null);
const fmtDate = (d) => {
  const js = toJSDate(d);
  return js && dayjs(js).isValid() ? dayjs(js).format("DD MMM YYYY") : "—";
};
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());

/* theme helpers */
const ACCENTS = [
  "#0d6efd","#3b82f6","#1d4ed8","#60a5fa",
  "#dc3545","#ef4444","#991b1b","#f87171",
  "#198754","#22c55e","#065f46","#86efac",
  "#ffc107","#facc15","#d97706","#fde047",
  "#6f42c1","#8b5cf6","#7e22ce","#c4b5fd",
  "#20c997","#06b6d4","#164e63","#67e8f9",
  "#fd7e14","#f97316","#9a3412","#fb923c",
  "#6c757d","#94a3b8","#334155","#e2e8f0",
  "#000000","#ffffff",
];
const normalizeHex6 = (hex) => {
  let v = String(hex || "").trim().replace(/^#/, "");
  if (v.length === 3) v = v.split("").map((x) => x + x).join("");
  return /^[0-9a-f]{6}$/i.test(v) ? `#${v.toLowerCase()}` : "#3b82f6";
};
const hexEq = (a, b) => normalizeHex6(a) === normalizeHex6(b);

/* ───────────────── Accent selector (stateless) ───────────────── */
function AccentDropdown({ active, onPick, hexToRgba }) {
  const activeHex = normalizeHex6(active);
  return (
    <Dropdown>
     
      <Dropdown.Menu style={{ padding: 10, maxHeight: 240, overflowY: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 24px)", gap: 8, minWidth: 230 }}>
          {ACCENTS.map((c) => {
            const col = normalizeHex6(c);
            const activeRing = hexEq(col, activeHex);
            return (
              <button
                key={col}
                type="button"
                title={col}
                onClick={() => onPick && onPick(col)}
                style={{
                  width: 20, height: 20, borderRadius: "50%", background: col,
                  border: "none", outline: "none", cursor: "pointer",
                  transition: "box-shadow .2s, transform .15s",
                  boxShadow: activeRing
                    ? `0 0 0 2px rgba(0,0,0,.35), 0 0 0 4px ${hexToRgba(col, .4)}`
                    : `0 0 0 1px rgba(0,0,0,.2)`,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              />
            );
          })}
          <label
            style={{
              width: 20, height: 20, borderRadius: "50%", cursor: "pointer",
              border: "1px dashed rgba(0,0,0,.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, color: "#666",
            }}
            title="Custom color"
          >
            +
            <input type="color" style={{ display: "none" }} onChange={(e) => onPick && onPick(e.target.value)} />
          </label>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default function Profile() {
  const brand = useBrand();
  const { primary, accent, setPrimary, setAccent } = brand || {};

  // normalized colors
  const primaryColor = useMemo(() => normalizeHex6(primary || "#3b82f6"), [primary]);
  const accentComputed = useMemo(
    () => normalizeHex6(accent || primaryColor),
    [accent, primaryColor]
  );

  // push to :root for global CSS vars
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", primaryColor);
    root.style.setProperty("--brand-accent", accentComputed);
  }, [primaryColor, accentComputed]);

  // theme picker handler (sync both for strong visual change)
  const onPickTheme = useCallback((c) => {
    const col = normalizeHex6(c);
    setPrimary?.(col);
    setAccent?.(col);
  }, [setPrimary, setAccent]);

  const [schoolId] = useState(() => localStorage.getItem("schoolId"));
  const [studentId] = useState(() => localStorage.getItem("studentId"));
  const [studentName] = useState(() => localStorage.getItem("studentName") || "");

  const [schoolName, setSchoolName] = useState("");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // alerts
  const [alertMsg, setAlertMsg] = useState(null);
  const [alertVariant, setAlertVariant] = useState("info");
  const showAlert = (msg, variant = "info", ms = 6000) => {
    setAlertVariant(variant);
    setAlertMsg(msg);
    if (ms) setTimeout(() => setAlertMsg(null), ms);
  };

  // edit contact modal
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState({ phone: "", address: "", email: "" });

  // change password modal
  const [showPw, setShowPw] = useState(false);
  const [pwForm, setPwForm] = useState({ old: "", neo: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [reveal, setReveal] = useState({ old: false, neo: false, confirm: false });

  // avatar
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // particles init
  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  // load school name
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!schoolId) return;
        const s = await getDoc(doc(db, "schools", schoolId));
        if (s.exists() && mounted) setSchoolName(s.data()?.name || "");
      } catch { /* noop */ }
    })();
    return () => { mounted = false; };
  }, [schoolId]);


  // live profile
  useEffect(() => {
    if (!schoolId || !studentId) {
      toast.error("Missing student session. Please sign in again.");
      return;
    }
    setLoading(true);
    const refDoc = doc(db, "schools", schoolId, "students", studentId);
    const unsub = onSnapshot(
      refDoc,
      (snap) => {
        if (!snap.exists()) {
          setProfile(null);
          toast.error("Could not find your student record.");
        } else {
          const data = { id: snap.id, ...snap.data() };
          setProfile(data);
          setForm({
            phone: data.phone || "",
            address: data.address || "",
            email: data.email || "",
          });
        }
        setLoading(false);
      },
      (err) => {
        console.error(err);
        toast.error("Failed to load profile.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [schoolId, studentId]);

  const classTag = useMemo(
    () => [profile?.className, profile?.section].filter(Boolean).join("-") || "—",
    [profile?.className, profile?.section]
  );

  const pickAvatar = () => fileRef.current?.click();
  const onFile = async (e) => {
    const user = getAuth().currentUser;
if (!user) { toast.error("Please sign in again."); return; }
await user.getIdToken(true);   
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/image\/(png|jpeg|webp)/.test(f.type)) return toast.info("Select a PNG/JPG/WEBP image");
    if (f.size > 2.5 * 1024 * 1024) return toast.info("Image too large (max ~2.5MB)");
    console.log("file type:", f.type, "size:", f.size);
    console.log(ref(storage, "probe").toString()); 
    console.log("auth uid:", getAuth().currentUser?.uid);
console.log("studentId:", studentId, "student.uid field:", profile?.uid);
    setUploading(true);
    await toast.promise(
      (async () => {
        const ext = f.name.split(".").pop()?.toLowerCase() || "jpg";
        const path = `schools/${schoolId}/students/${studentId}/avatar_${Date.now()}.${ext}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, f, { contentType: f.type });
        const url = await getDownloadURL(storageRef);
        await updateDoc(doc(db, "schools", schoolId, "students", studentId), {
           uid: getAuth().currentUser.uid ,photoURL: url, updatedAt: serverTimestamp(),
        });
      })(),
      { pending: "Uploading photo…", success: "Photo updated", error: "Upload failed" }
    );
    setUploading(false);
    e.target.value = "";
  };

  const sendReset = async () => {
    const email = profile?.email;
    if (!email) return toast.info("No email on file.");
    await toast.promise(
      sendPasswordResetEmail(getAuth(), email),
      { pending: "Sending reset email…", success: `Reset email sent to ${email}`, error: "Could not send reset email" }
    );
  };

  const doChangePassword = async () => {
    const email = profile?.email || "";
    const { old, neo, confirm } = pwForm;
    if (!email) return toast.info("No email linked.");
    if (!old || !neo || !confirm) return toast.info("Fill all fields.");
    if (neo.length < 6) return toast.info("New password too short.");
    if (neo !== confirm) return toast.info("Passwords do not match.");

    const auth = getAuth();
    const user = auth.currentUser;
   if (profile && !profile.uid && auth.currentUser?.uid) {
  updateDoc(doc(db, "schools", schoolId, "students", studentId), {
    uid: auth.currentUser.uid,
    updatedAt: serverTimestamp()
  }).catch(() => {});
   }
    setPwBusy(true);
    try {
      await toast.promise(
        (async () => {
          const cred = EmailAuthProvider.credential(email, old);
          await reauthenticateWithCredential(user, cred);
          await updatePassword(user, neo);
        })(),
        { pending: "Updating password…", success: "Password changed", error: "Password update failed" }
      );
      setShowPw(false);
      setPwForm({ old: "", neo: "", confirm: "" });
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to change password.");
    } finally {
      setPwBusy(false);
    }
  };

  const saveContact = async () => {
    const nextPhone = String(form.phone || "").trim();
    const nextAddress = String(form.address || "").trim();
    const nextEmail = String(form.email || "").trim();
    if (nextEmail && !isEmail(nextEmail)) return toast.info("Invalid email.");

    const updates = { phone: nextPhone, address: nextAddress, email: nextEmail, updatedAt: serverTimestamp() };
    const prevEmail = String(profile?.email || "").trim();
    const emailChanged = !!nextEmail && nextEmail.toLowerCase() !== prevEmail.toLowerCase();
    const auth = getAuth();
    const user = auth.currentUser;

    try {
      if (emailChanged) {
        const ok = window.confirm(`Change email from "${prevEmail || "—"}" to "${nextEmail}"?\nYou may need to re-login.`);
        if (!ok) return;

        await toast.promise(
          (async () => {
            try {
              if (user) {
                await updateEmail(user, nextEmail);
              } else {
                showAlert("Could not verify session to update login email. Only profile email will be changed.", "warning");
              }
            } catch (e) {
              if (e?.code === "auth/requires-recent-login") {
                showAlert("For security, please sign in again and then change your email.", "warning");
              } else if (e?.code === "auth/email-already-in-use") {
                throw new Error("That email is already in use.");
              } else if (e?.code === "auth/invalid-email") {
                throw new Error("Invalid email address.");
              } else {
                throw new Error(e?.message || "Failed to update login email.");
              }
            }
            await updateDoc(doc(db, "schools", schoolId, "students", studentId), updates);
          })(),
          { pending: "Saving contact…", success: "Contact updated", error: "Contact update failed" }
        );
        setShowEdit(false);
        showAlert("Email changed. If you’re signed out, log in using the new email.", "info");
      } else {
        await toast.promise(
          updateDoc(doc(db, "schools", schoolId, "students", studentId), updates),
          { pending: "Saving contact…", success: "Contact updated", error: "Contact update failed" }
        );
        setShowEdit(false);
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Could not update contact info.");
    }
  };

  return (
    <div className="position-relative overflow-hidden">
      {/* FULL-PAGE background */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{
          zIndex: 0,
          background: `
            radial-gradient(1100px 600px at 8% 12%, ${hexToRgba(primaryColor, .10)} 0%, transparent 60%),
            radial-gradient(1000px 560px at 92% 88%, ${hexToRgba(accentComputed,  .10)} 0%, transparent 60%)
          `,
        }}
      />
      <Particles
        id="profile-bg-particles"
        className="position-fixed top-0 start-0 w-100 h-100"
        init={particlesInit}
        style={{ zIndex: 1, pointerEvents: "none" }}
        options={{
          fpsLimit: 60,
          fullScreen: { enable: false },
          background: { color: { value: "transparent" } },
          detectRetina: true,
          interactivity: {
            events: { onHover: { enable: true, mode: "repulse" }, onClick: { enable: true, mode: "push" }, resize: true },
            modes: { repulse: { distance: 120, duration: 0.35 }, push: { quantity: 2 } },
          },
          particles: {
            number: { value: 70, density: { enable: true, area: 900 } },
            color: { value: [primaryColor, accentComputed] },
            links: { enable: true, color: hexToRgba(primaryColor, 0.45), distance: 140, opacity: 0.45, width: 1 },
            move: { enable: true, speed: 1.1, outModes: { default: "out" } },
            opacity: { value: 0.55 },
            size: { value: { min: 1, max: 3 } },
          },
          responsive: [
            { maxWidth: 768, options: { particles: { number: { value: 40 } } } },
            { maxWidth: 420, options: { particles: { number: { value: 24 } } } },
          ],
        }}
      />

      {/* Foreground */}
      <div className="p-2 p-sm-3 position-relative" style={{ zIndex: 2 }}>
        <ToastContainer position="bottom-right" newestOnTop closeOnClick pauseOnFocusLoss draggable />
        {alertMsg && <Alert variant={alertVariant} dismissible onClose={() => setAlertMsg(null)} className="mb-3">{alertMsg}</Alert>}

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex flex-column">
            <h5 className="mb-0">Profile</h5>
            <small className="text-muted">
              {schoolName ? `${studentName || "Student"} • ${schoolName}` : studentName || "Student"}
            </small>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Badge bg="light" text="dark" className="border d-none d-sm-inline">
              {classTag !== "—" ? `Class ${classTag}` : "Student"}
            </Badge>
            <AccentDropdown active={primaryColor} onPick={onPickTheme} hexToRgba={hexToRgba} />
          </div>
        </div>

        {/* Card content */}
        <Card className="border-0 shadow-sm profile-card">
          <Card.Body>
            {loading ? (
              <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</div>
            ) : !profile ? (
              <div className="text-muted">No profile found.</div>
            ) : (
              <>
                {/* Avatar + identity */}
                <Row className="g-3 align-items-center">
                  <Col xs="auto">
                    {profile.photoURL ? (
                      <div
                        className="rounded-3"
                        style={{
                          padding: 3, width: 96, height: 96,
                          borderRadius: 12,
                        }}
                      >
                        <img
                          src={profile.photoURL}
                          alt={profile.name}
                          className="rounded-3 border bg-white"
                          style={{ width: "100%", height: "100%", objectFit: "cover", borderColor: hexToRgba(primaryColor,.35) }}
                        />
                      </div>
                    ) : (
                      <div className="d-inline-flex align-items-center justify-content-center rounded-3 border"
                           style={{ width: 96, height: 96, background: hexToRgba(primaryColor,.08), borderColor: hexToRgba(primaryColor,.28) }}>
                        <i className="bi bi-person fs-1" style={{ color: hexToRgba(primaryColor,.7) }} />
                      </div>
                    )}
                  </Col>
                  <Col>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="fs-5 fw-semibold">{safe(profile.name)}</span>
                      <Badge bg={profile.status === "inactive" ? "secondary" : "success"}>
                        {profile.status === "inactive" ? "Inactive" : "Active"}
                      </Badge>
                    </div>
                    <div className="text-muted small mt-1">
                      Class: {classTag} • Roll: {safe(profile.rollNo)} • Admission: {safe(profile.admissionNo)}
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <Button size="sm" className="btn-neo" onClick={pickAvatar} disabled={uploading}>
                        <i className="bi bi-image me-1" /> {uploading ? "Uploading…" : "Change photo"}
                      </Button>
                      <input ref={fileRef} type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={onFile} />
                      <Button size="sm" className="btn-ghost" onClick={() => setShowEdit(true)}>
                        <i className="bi bi-pencil-square me-1" /> Edit contact
                      </Button>
                      <Button size="sm" className="btn-ghost" onClick={() => setShowPw(true)}>
                        <i className="bi bi-shield-lock me-1" /> Change password
                      </Button>
                      <Button size="sm" className="btn-ghost-dark" onClick={sendReset}>
                        <i className="bi bi-envelope-paper me-1" /> Send reset link
                      </Button>
                    </div>
                  </Col>
                </Row>

                <hr className="my-4" />

                {/* Details */}
                <Row className="g-3">
                  <Col md={6} lg={4}><Detail label="Email" value={profile.email} copyable /></Col>
                  <Col md={6} lg={4}><Detail label="Phone" value={profile.phone} /></Col>
                  <Col md={12} lg={4}><Detail label="DOB" value={fmtDate(profile.dob)} /></Col>
                  <Col md={6} lg={4}><Detail label="Gender" value={safe(profile.gender)} /></Col>
                  <Col md={6} lg={4}><Detail label="Status" value={profile.status === "inactive" ? "Inactive" : "Active"} /></Col>
                  <Col md={12} lg={4}><Detail label="Class / Section" value={classTag} /></Col>
                  <Col md={6} lg={4}><Detail label="Branch" value={safe(profile.branch)} /></Col>
                  <Col md={6} lg={4}><Detail label="Roll No." value={safe(profile.rollNo)} /></Col>
                  <Col md={6} lg={4}><Detail label="Admission No." value={safe(profile.admissionNo)} /></Col>
                  <Col md={12} lg={4}><Detail label="Student ID" value={profile.id} copyable /></Col>
                  <Col md={12}><Detail label="Address" value={safe(profile.address)} wide /></Col>
                </Row>
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Edit contact modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Contact</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={form.email}
                onChange={(e) => change("email", e.target.value)}
                placeholder="name@example.com"
              />
              <Form.Text className="text-muted">
                Changing email also attempts to update your login email.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                value={form.phone}
                onChange={(e) => change("phone", e.target.value)}
                placeholder="+91 90000 00000"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={form.address}
                onChange={(e) => change("address", e.target.value)}
                placeholder="Street, City"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-warning" onClick={() => setShowEdit(false)}>Cancel</Button>
          <Button className="btn-neo" onClick={saveContact}>Save</Button>
        </Modal.Footer>
      </Modal>

      {/* Change password modal */}
      <Modal show={showPw} onHide={() => setShowPw(false)} centered>
        <Modal.Header closeButton><Modal.Title>Change Password</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Label className="mb-1">Current password</Form.Label>
            <InputGroup className="mb-3">
              <Form.Control
                type={reveal.old ? "text" : "password"}
                value={pwForm.old}
                onChange={(e) => setPwForm((f) => ({ ...f, old: e.target.value }))}
                autoComplete="current-password"
              />
              <Button
                className="btn-ghost"
                onClick={() => setReveal((r) => ({ ...r, old: !r.old }))}
                title={reveal.old ? "Hide" : "Show"}
              >
                <i className={`bi ${reveal.old ? "bi-eye-slash" : "bi-eye"}`} />
              </Button>
            </InputGroup>

            <Form.Label className="mb-1">New password</Form.Label>
            <InputGroup className="mb-3">
              <Form.Control
                type={reveal.neo ? "text" : "password"}
                value={pwForm.neo}
                onChange={(e) => setPwForm((f) => ({ ...f, neo: e.target.value }))}
                autoComplete="new-password"
                placeholder="At least 6 characters"
              />
              <Button
                className="btn-ghost"
                onClick={() => setReveal((r) => ({ ...r, neo: !r.neo }))}
                title={reveal.neo ? "Hide" : "Show"}
              >
                <i className={`bi ${reveal.neo ? "bi-eye-slash" : "bi-eye"}`} />
              </Button>
            </InputGroup>

            <Form.Label className="mb-1">Confirm new password</Form.Label>
            <InputGroup>
              <Form.Control
                type={reveal.confirm ? "text" : "password"}
                value={pwForm.confirm}
                onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
                autoComplete="new-password"
              />
              <Button
                className="btn-ghost"
                onClick={() => setReveal((r) => ({ ...r, confirm: !r.confirm }))}
                title={reveal.confirm ? "Hide" : "Show"}
              >
                <i className={`bi ${reveal.confirm ? "bi-eye-slash" : "bi-eye"}`} />
              </Button>
            </InputGroup>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-warning" onClick={() => setShowPw(false)} disabled={pwBusy}>Cancel</Button>
          <Button className="btn-neo" onClick={doChangePassword} disabled={pwBusy}>
            {pwBusy ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Styles */}
      <style>{`
        .profile-card .card-body {
          background:
            linear-gradient(135deg, ${hexToRgba(primaryColor,.07)} 0%, ${hexToRgba(accentComputed,.07)} 100%);
          border-radius: 16px;
        }

        /* Detail blocks */
        .detail-item{
          border: 1px solid ${hexToRgba(primaryColor,.25)};
          border-radius: 12px;
          padding: .75rem .9rem;
          background: linear-gradient(180deg, ${hexToRgba(primaryColor,.04)} 0%, ${hexToRgba(primaryColor,.02)} 100%);
          transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease;
        }
        .detail-item:hover{
          transform: translateY(-1px);
          border-color: ${hexToRgba(primaryColor,.35)};
          box-shadow: 0 8px 22px ${hexToRgba(primaryColor,.15)};
        }
        .detail-label{ font-size: .8rem; color: var(--bs-secondary-color); }
        .detail-value{ font-weight: 600; }

        /* Neo buttons (gradient, soft glow) */
        .btn-neo{
          --_bg1: ${hexToRgba(primaryColor, .95)};
          --_bg2: ${hexToRgba(accentComputed, .95)};
          color:#fff !important;
          background: linear-gradient(135deg, var(--_bg1) 0%, var(--_bg2) 100%);
          border: 1px solid ${hexToRgba(primaryColor,.45)};
          box-shadow: 0 10px 18px ${hexToRgba(primaryColor,.24)};
          transition: transform .12s ease, box-shadow .12s ease, filter .12s ease;
        }
        .btn-neo:hover{
          transform: translateY(-1px);
          filter: brightness(1.04);
          box-shadow: 0 14px 26px ${hexToRgba(primaryColor,.30)};
        }
        .btn-neo:active{
          transform: translateY(0);
          filter: brightness(.98);
          box-shadow: 0 6px 14px ${hexToRgba(primaryColor,.24)} inset;
        }

        /* Ghost buttons (tinted outline) */
        .btn-ghost{
          color: ${primaryColor} !important;
          background: transparent;
          border: 1px solid ${hexToRgba(primaryColor,.45)};
          transition: transform .12s ease, background .12s ease, box-shadow .12s ease, color .12s ease;
        }
        .btn-ghost:hover{
          background: ${hexToRgba(primaryColor,.10)};
          box-shadow: 0 6px 16px ${hexToRgba(primaryColor,.18)};
          transform: translateY(-1px);
        }
        .btn-ghost:active{
          background: ${hexToRgba(primaryColor,.16)};
          transform: translateY(0);
        }

        /* Dark ghost for neutral actions */
        .btn-ghost-dark{
          color: var(--bs-body-color) !important;
          background: transparent;
          border: 1px solid rgba(0,0,0,.25);
          transition: transform .12s ease, background .12s ease, box-shadow .12s ease;
        }
        .btn-ghost-dark:hover{
          background: rgba(0,0,0,.05);
          box-shadow: 0 6px 16px rgba(0,0,0,.12);
          transform: translateY(-1px);
        }

        /* Card subtle float */
        .profile-card{
          border-radius: 16px;
          box-shadow: 0 14px 32px ${hexToRgba(primaryColor, .14)};
        }
      `}</style>
    </div>
  );
}

/* --------- tiny detail helper --------- */
function Detail({ label, value, copyable = false, wide = false }) {
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      toast.success("Copied");
    } catch { /* noop */ }
  };
  return (
    <div className={`detail-item ${wide ? "" : ""}`}>
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div>
          <div className="detail-label">{label}</div>
          <div className="detail-value">{value || "—"}</div>
        </div>
        {copyable && value ? (
          <Button size="sm" className="btn-ghost" onClick={onCopy} title="Copy">
            <i className="bi bi-clipboard" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
